import React, { useEffect, useState } from 'react';
import { Alert } from '@smarterservices/smarter-ui';
import BlockRenderer from './BlockRenderer';
import Loader from '@ui/context/session/components/Common/Loader';

// @ts-ignore - ClientHttpRequester doesn't have type definitions
import { ClientHttpRequester } from '@smarterservices/client-http-requester';

/**
 * Error types for BlockRemoteRenderer
 */
export type BlockRemoteRendererErrorType = 'fetch' | 'render';

/**
 * Extended Error with type information
 */
export interface BlockRemoteRendererError extends Error {
    type: BlockRemoteRendererErrorType;
    originalError?: Error;
}

/**
 * BlockRemoteRenderer Component
 * 
 * Fetches BlockKit JSON from a remote URL and renders it using the BlockRenderer.
 * Automatically appends provided data as query parameters to the URL.
 * 
 * This component handles the complete lifecycle of fetching and rendering remote
 * BlockKit content, including loading states, error handling, and empty content states.
 * 
 * **Important:** Like BlockRenderer, this component should be wrapped in a BlockKitProvider
 * if you need to use replacements or other BlockKit context features.
 * 
 * @component
 * @param {object} props - Component properties
 * 
 * @param {string} props.url - URL to fetch BlockKit JSON from (required)
 * @param {string} [props.contentId] - Unique identifier for the content being rendered
 * @param {object} [props.data] - Key-value pairs to append as query parameters to the URL
 * @param {function} [props.onLoaded] - Callback fired when content is successfully loaded. Receives the blocks data.
 * @param {function} [props.onLoading] - Callback that returns custom loading content. If not provided, displays default Loader with "Loading..." text.
 * @param {function} [props.onEmpty] - Callback that returns custom empty state content. If not provided, displays default "No content available" message.
 * @param {function} [props.onError] - Callback fired when an error occurs. Receives BlockRemoteRendererError with type property ('fetch' for URL/network errors, 'render' for BlockKit rendering errors). Can return custom error content (JSX or string). If null/undefined is returned, displays standard error message with error type.
 * 
 * @returns {JSX.Element} The rendered BlockKit content or appropriate state UI
 * 
 * @example
 * // Basic usage
 * <BlockRemoteRenderer
 *   url="https://api.example.com/blockkit/content"
 *   contentId="my-content"
 * />
 * 
 * @example
 * // With query parameters and error type handling
 * <BlockRemoteRenderer
 *   url="https://api.example.com/blockkit/content"
 *   contentId="session-details"
 *   data={{
 *     sessionSid: "session-123",
 *     installSid: "install-456",
 *     examSid: "exam-789"
 *   }}
 *   onLoaded={(blocks) => console.log('Loaded blocks:', blocks)}
 *   onError={(error) => {
 *     if (error.type === 'fetch') {
 *       return <div>Failed to load content from server: {error.message}</div>;
 *     } else {
 *       return <div>Failed to render BlockKit content: {error.message}</div>;
 *     }
 *   }}
 * />
 * 
 * @example
 * // With custom loading and empty states
 * <BlockRemoteRenderer
 *   url="https://api.example.com/blockkit/wizard"
 *   contentId="wizard-flow"
 *   onLoading={() => (
 *     <div style={{ padding: '2rem', textAlign: 'center' }}>
 *       <Spinner size="large" />
 *       <p>Loading wizard steps...</p>
 *     </div>
 *   )}
 *   onEmpty={() => (
 *     <Alert variant="info">
 *       No wizard steps configured. Please contact support.
 *     </Alert>
 *   )}
 * />
 * 
 * @example
 * // With BlockKitProvider for replacements
 * <BlockKitProvider replacements={{ userName: "John Doe", sessionId: "123" }}>
 *   <BlockRemoteRenderer
 *     url="https://api.example.com/blockkit/wizard"
 *     contentId="wizard-flow"
 *   />
 * </BlockKitProvider>
 */
function BlockRemoteRenderer({
    url,
    contentId,
    data = {},
    onLoaded,
    onLoading,
    onEmpty,
    onError
}: BlockRemoteRendererProps) {
    const [blocks, setBlocks] = useState<any[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<BlockRemoteRendererError | null>(null);
    const [renderError, setRenderError] = useState<BlockRemoteRendererError | null>(null);

    useEffect(() => {
        const fetchBlockKit = async () => {
            if (!url) {
                const err: BlockRemoteRendererError = Object.assign(
                    new Error('No URL provided for BlockRemoteRenderer'),
                    { type: 'fetch' as BlockRemoteRendererErrorType }
                );
                setError(err);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Build URL with query parameters from data object
                const fetchUrl = new URL(url);
                Object.entries(data).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        fetchUrl.searchParams.set(key, String(value));
                    }
                });

                // Add contentId if provided
                if (contentId) {
                    fetchUrl.searchParams.set('contentId', contentId);
                }

                const finalUrl = fetchUrl.toString();

                // Fetch the BlockKit content
                const requester = ClientHttpRequester({
                    apiUrl: finalUrl,
                    retry: {
                        enabled: true,
                    },
                });
                const response = await requester.get();

                // Check if request was successful
                if (response.status !== 200) {
                    const fetchError: BlockRemoteRendererError = Object.assign(
                        new Error(response.error || `HTTP error! status: ${response.status}`),
                        { type: 'fetch' as BlockRemoteRendererErrorType }
                    );
                    throw fetchError;
                }

                const responseData = response.data;

                // Validate that we received data
                if (!responseData) {
                    const fetchError: BlockRemoteRendererError = Object.assign(
                        new Error('No data received from URL'),
                        { type: 'fetch' as BlockRemoteRendererErrorType }
                    );
                    throw fetchError;
                }

                // Handle both array and single block formats
                const blocksData = Array.isArray(responseData) ? responseData : [responseData];

                setBlocks(blocksData);
                setLoading(false);

                // Call onLoaded callback if provided
                if (onLoaded) {
                    onLoaded(blocksData);
                }
            } catch (err) {
                console.error('BlockRemoteRenderer fetch error:', err);
                const error: BlockRemoteRendererError = (err as any).type
                    ? (err as BlockRemoteRendererError)
                    : Object.assign(
                        err instanceof Error ? err : new Error(String(err)),
                        { type: 'fetch' as BlockRemoteRendererErrorType }
                    );
                setError(error);
                setLoading(false);
            }
        };

        fetchBlockKit();
    }, [url, contentId, JSON.stringify(data), onLoaded]);

    // Loading state
    if (loading) {
        // Check if onLoading callback is provided
        if (onLoading) {
            const customLoadingContent = onLoading();
            if (customLoadingContent !== null && customLoadingContent !== undefined) {
                return <>{customLoadingContent}</>;
            }
        }

        // Default loading display
        // @ts-ignore - React version mismatch
        return <Loader spinnerSize="medium" text="Loading..." />;
    }

    // Error state (fetch or render)
    const displayError = error || renderError;
    if (displayError) {
        // Check if onError callback is provided and returns custom content
        if (onError) {
            const customErrorContent = onError(displayError);
            if (customErrorContent !== null && customErrorContent !== undefined) {
                return <>{customErrorContent}</>;
            }
        }

        // Default error display
        return (
            <Alert variant="error" margin="small" hasShadow={false}>
                <div>
                    <strong>
                        {displayError.type === 'fetch' ? 'Failed to load content' : 'Failed to render content'}
                    </strong>
                    <div style={{ marginTop: '0.5rem' }}>{displayError.message}</div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.8 }}>
                        Error Type: {displayError.type}
                    </div>
                    {url && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.8 }}>
                            URL: {url}
                        </div>
                    )}
                    {contentId && (
                        <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                            Content ID: {contentId}
                        </div>
                    )}
                </div>
            </Alert>
        );
    }

    // No content state
    if (!blocks || blocks.length === 0) {
        // Check if onEmpty callback is provided
        if (onEmpty) {
            const customEmptyContent = onEmpty();
            if (customEmptyContent !== null && customEmptyContent !== undefined) {
                return <>{customEmptyContent}</>;
            }
        }

        // Default empty state display
        return (
            <Alert variant="info" margin="small" hasShadow={false}>
                No content available
            </Alert>
        );
    }

    // Render BlockKit content with error boundary
    try {
        {/* @ts-ignore - React version mismatch */ }
        return (<BlockRenderer blocks={blocks} />);
    } catch (err) {
        // Catch render errors
        console.error('BlockRemoteRenderer render error:', err);
        const renderErr: BlockRemoteRendererError = Object.assign(
            err instanceof Error ? err : new Error(String(err)),
            { type: 'render' as BlockRemoteRendererErrorType, originalError: err instanceof Error ? err : undefined }
        );
        
        // Set render error to trigger error display
        if (!renderError) {
            setRenderError(renderErr);
        }
        
        // Call onError if provided
        if (onError) {
            const customErrorContent = onError(renderErr);
            if (customErrorContent !== null && customErrorContent !== undefined) {
                return <>{customErrorContent}</>;
            }
        }
        
        // Default render error display
        return (
            <Alert variant="error" margin="small" hasShadow={false}>
                <div>
                    <strong>Failed to render content</strong>
                    <div style={{ marginTop: '0.5rem' }}>{renderErr.message}</div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.8 }}>
                        Error Type: render
                    </div>
                    {contentId && (
                        <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                            Content ID: {contentId}
                        </div>
                    )}
                </div>
            </Alert>
        );
    }
}

/**
 * Props for BlockRemoteRenderer component
 */
export interface BlockRemoteRendererProps {
    /** URL to fetch BlockKit JSON from */
    url: string;
    /** Unique identifier for the content being rendered */
    contentId?: string;
    /** Key-value pairs to append as query parameters to the URL */
    data?: Record<string, any>;
    /** Callback fired when content is successfully loaded */
    onLoaded?: (blocks: any[]) => void;
    /** Callback that returns custom loading content. If not provided, displays default Loader with "Loading..." text. */
    onLoading?: () => React.ReactNode;
    /** Callback that returns custom empty state content. If not provided, displays default "No content available" message. */
    onEmpty?: () => React.ReactNode;
    /** Callback fired when an error occurs. Receives error with type ('fetch' or 'render'). Return custom error content or null for default. */
    onError?: (error: BlockRemoteRendererError) => React.ReactNode;
}

export default BlockRemoteRenderer;
